import { useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { QueryConfig } from "@/lib/react-query";
import { ApiContext } from "@/pages/hendren/api/context/ApiContextProvider";
import Palette from "@/Utils/Colors/palette";
import { formatProductHierarchy } from "@/Utils/formatProductHierarchy";

export interface MarketingDataPoint {
  brand: string;
  category: string;
  product: string;
  channel: string;
  geography?: string;
  data: {
    confidence: number;
    currentSpend: number;
    lowerBound: number;
    upperBound: number;
    pointOfMaximumSpendEfficiency: number;
    totalRevenue: number;
    totalSpend: number;
    totalUnits: number;
    activeWeeks: number;
  };
}

export interface ExperimentDetails {
  design: string;
  hypothesis: string;
  measurement: string;
  timeframe: string;
  roi: string;
  potentialIncreaseAmount: string;
  potentialIncreaseText: string;
  riskLevel: string;
  timeframeLabel: string;
  description: string;
}

export interface TestingRecommendationsResult {
  generatedResult: {
    recommendations: TransformedExperiment[];
  };
  data: MarketingDataPoint[];
  endDate: string;
  startDate: string;
}

export interface TestingRecommendationsResponse {
  data: TestingRecommendationsResult;
}

export interface TestingRecommendationTag {
  id: string;
  name: string;
  color: string;
  tooltip?: string;
}
// Interface for the transformed data that matches the Experiment interface
export interface TransformedExperiment {
  id: string;
  title: string;
  description: string;
  tags: TestingRecommendationTag[];
  details: ExperimentDetails;
  brand: string;
  category: string;
  product: string;
  channels: string[];
}

export interface TransformedTestingRecommendationsResult {
  experiments: TransformedExperiment[];
  marketingData: MarketingDataPoint[];
  endDate: string;
  startDate: string;
}

export const useTestingRecommendations = ({
  config,
  skipCache = false,
}: {
  config?: QueryConfig<any>;
  skipCache?: boolean;
} = {}) => {
  const apiContext = useContext(ApiContext);
  if (!apiContext) {
    throw new Error("ApiContext must be used within ApiContextProvider");
  }
  const { apiClient } = apiContext;
  const { mapDefaultNodeName } = useDefaultNodeNameInfo();
  const theme = useTheme();

  return useQuery({
    ...config,
    queryKey: ["testing-recommendations", skipCache],
    queryFn: async () => {
      return apiClient.get("/api/v1/testing-recommendations", {
        params: {
          skipCache,
        },
      });
    },
    select: ({ data }: TestingRecommendationsResponse): TransformedTestingRecommendationsResult => {
      const palette = new Palette(theme.palette.ROILineChart.barChartColors);

      const experiments: TransformedExperiment[] =
        data.generatedResult.recommendations?.map((rec, index) => {
          const channelColor = palette.getSequentialColor(index);

          const channelTag = rec.channels.length === 1 ? rec.channels[0] : "Multiple";
          let tagName;
          if (rec.product !== "default") {
            tagName = rec.product;
          } else if (rec.category !== "default") {
            tagName = rec.category;
          } else {
            tagName = mapDefaultNodeName(rec.brand);
          }

          return {
            id: rec.id,
            title: rec.title,
            description: rec.description,
            brand: rec.brand,
            category: rec.category,
            product: rec.product,
            channels: rec.channels,
            tags: [
              {
                id: tagName,
                name: tagName,
                color: "#6E6972",
                tooltip: formatProductHierarchy(rec.brand, rec.category, rec.product),
              },
              {
                id: channelTag,
                name: channelTag,
                color: channelColor.color,
                tooltip: rec.channels.join(", "),
              },
            ],
            details: rec.details,
          };
        }) || [];

      return {
        experiments,
        marketingData: data.data,
        endDate: data.endDate,
        startDate: data.startDate,
      };
    },
  });
};
